import { useEffect, useState } from 'react';
import { Button } from '../../../../../components/Button';
import { Modal } from '../../../../../components/Modal';
import { Select } from '../../../../../components/inputs/Select';
import { accessLevelsApi } from '../../../../administration/access-levels/accessLevelsApi';
import type { AccessLevel } from '../../../../administration/access-levels/accessLevelTypes';
import { ApiError } from '../../../../../lib/apiError';
import { focusValidationError } from '../../../../../lib/validationFocus';
import { personnelApi } from '../../personnelApi';
import type { PersonnelDetail } from '../../personnelTypes';

/** The sentinel the picker uses for "unclassified", which is a real choice and not a blank. */
const NONE = 'none';

/**
 * Classifies one 201 file, or clears its classification.
 *
 * **Its own modal because it is its own key.** `personnel.update` corrects what the record
 * says; `personnel.set-access-level` decides who may read it at all, and the two are not the
 * same privilege — raising a file out of sight of the clerks who maintain it is a decision
 * about disclosure.
 *
 * **A reader can classify a file out of their own sight, and that is deliberate.** Someone
 * ranked Level 2 who raises this record to Level 1 loses it on save, and the sentence below
 * says so before the tick. Refusing would mean a reader could only classify as far as their own
 * clearance, which is the opposite of what the key is for: the person who decides a file is
 * sensitive is frequently not the person cleared to read it.
 *
 * **No Autogenerate, under the documented exemption**: the picker opens on the record's current
 * classification, so the form is already filled at mount and the contract is met before the
 * button would have anything to do. The field is also a decision about who may read a named
 * person's record, which is not a thing a generator could fill honestly.
 */
export function EditAccessLevelModal({
  record,
  onClose,
  onSaved,
}: {
  /** Null keeps the modal closed — the shape every other section editor here takes. */
  record: PersonnelDetail | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  return record ? (
    <AccessLevelForm key={record.id} record={record} onClose={onClose} onSaved={onSaved} />
  ) : null;
}

function AccessLevelForm({
  record,
  onClose,
  onSaved,
}: {
  record: PersonnelDetail;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Seeded once, on mount — the `key` above remounts this for a different record.
  const [levelId, setLevelId] = useState<string>(record.accessLevelId ?? NONE);

  const [levels, setLevels] = useState<AccessLevel[]>([]);
  const [loadingLevels, setLoadingLevels] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    accessLevelsApi
      .options(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setLevels(next);
      })
      // One focusValidationError call per catch, in the save path only.
      .catch(() => undefined)
      .finally(() => {
        if (!controller.signal.aborted) setLoadingLevels(false);
      });

    return () => controller.abort();
  }, []);

  const chosen = levelId === NONE ? null : levelId;
  const chosenLevel = levels.find((l) => l.id === chosen) ?? null;
  const canSave = confirmed && !saving && !loadingLevels;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await personnelApi.setAccessLevel(record.id, { accessLevelId: chosen });
      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not set the access level.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'personnel-access-level' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Set access level"
      size="md"
      id="personnel-access-level-modal"
      confirm={{
        id: 'personnel-access-level-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button
            id="personnel-access-level-cancel"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>

          <Button
            id="personnel-access-level-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <Select
          id="personnel-access-level-access-level-id"
          label="Access level"
          description="The clearance a reader needs to open this file. A stronger rung is read by fewer people."
          value={levelId}
          onChange={(next) => setLevelId(next || NONE)}
          loading={loadingLevels}
          disabled={saving}
          // The ladder's own order, strongest first — alphabetically "Level 10" sorts above
          // "Level 2", and the order IS the meaning here.
          sort="none"
          clearable={false}
          options={[
            {
              value: NONE,
              label: 'Unclassified',
              sublabel: 'Readable by everyone who may read personnel records',
            },
            ...levels.map((level) => ({
              value: level.id,
              label: level.name,
              sublabel: level.description ?? undefined,
              right: <span className="font-mono tabular-nums">Rank {level.rank}</span>,
            })),
          ]}
          error={error?.fieldError('accessLevelId')}
        />

        {/*
          The consequence, on the form and before the tick, in the record's own terms. The
          second sentence is the one that matters: this form can take the file away from the
          reader filling it in, and nothing else on screen would say so.
        */}
        <p className="text-xs text-slate-500">
          {chosenLevel
            ? `Only readers tagged ${chosenLevel.name} or stronger will be able to open this record. Everyone else still sees it listed and counted, with the name withheld.`
            : 'Everyone who may read personnel records will be able to open this one.'}{' '}
          If your own clearance does not reach the level you choose, you will lose access to this
          record when you submit.
        </p>
      </div>
    </Modal>
  );
}
